import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Clear, RecordVoiceOver, Search } from "@mui/icons-material";
import { usePageHeader } from "../../context/LayoutContext";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { useListTranscripts } from "./hooks/useListTranscripts";
import { TranscriptGrid } from "./components/TranscriptGrid";

const TranscriptsListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  // Debounce before hitting the server so we don't fire a request per keystroke;
  // the search itself runs server-side (see SEARCH_BACKEND_SPEC.md).
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { transcripts, isLoading, isInitialLoad, hasMore, loadMore } =
    useListTranscripts(debouncedSearch);

  usePageHeader({
    title: "Transcripts",
    subtitle: "Browse and edit transcripts from bodycam evidence",
    icon: <RecordVoiceOver />,
    chip: { label: `${transcripts.length} transcripts` },
  });

  return (
    <Container
      sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto", py: 2 }}
    >
      <Grid
        container
        spacing={3}
        direction="column"
        sx={{ display: "flex", alignItems: "flex-start" }}
      >
        <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: 480 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by file name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm("")}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />
          </Box>
        </Grid>

        <TranscriptGrid
          isLoading={isLoading || isInitialLoad}
          transcripts={transcripts}
          hasMore={hasMore}
          onLoadMore={loadMore}
          hasActiveFilters={!!debouncedSearch.trim()}
        />
      </Grid>
    </Container>
  );
};

export default TranscriptsListPage;
